import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  FaPlus,
  FaTrash,
  FaList,
  FaTimes,
  FaCheck,
  FaBook,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const getAuthToken = () => localStorage.getItem('authToken');

const Journal = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([
    { account_id: '', account_name: '', debit: '', credit: '', remark: '' },
  ]);
  const [accounts, setAccounts] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showDropdowns, setShowDropdowns] = useState({});
  const [filteredAccounts, setFilteredAccounts] = useState({});
  const dropdownRefs = useRef({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach((key) => {
        if (
          dropdownRefs.current[key] &&
          !dropdownRefs.current[key].contains(event.target)
        ) {
          setShowDropdowns((prev) => ({ ...prev, [key]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-accounts?all=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAccounts(response.data.data);
      const initialFiltered = {};
      entries.forEach((_, index) => {
        initialFiltered[index] = response.data.data;
      });
      setFilteredAccounts(initialFiltered);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAlert({
        show: true,
        message: 'Error loading accounts.',
        variant: 'danger',
      });
    }
  };

  const handleAccountChange = (index, value) => {
    const updatedEntries = [...entries];
    updatedEntries[index].account_name = value;
    updatedEntries[index].account_id = '';
    setEntries(updatedEntries);

    if (value.trim()) {
      const filtered = accounts.filter((acc) =>
        acc.name.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredAccounts((prev) => ({ ...prev, [index]: filtered }));
      setShowDropdowns((prev) => ({ ...prev, [index]: true }));
    } else {
      setFilteredAccounts((prev) => ({ ...prev, [index]: accounts }));
      setShowDropdowns((prev) => ({ ...prev, [index]: false }));
    }
  };

  const selectAccount = (index, account) => {
    const updatedEntries = [...entries];
    updatedEntries[index].account_id = account.id;
    updatedEntries[index].account_name = account.name;
    setEntries(updatedEntries);
    setShowDropdowns((prev) => ({ ...prev, [index]: false }));
  };

  const handleChange = (index, field, value) => {
    const updatedEntries = [...entries];
    updatedEntries[index][field] = value;
    setEntries(updatedEntries);
  };

  const addEntry = () => {
    const newIndex = entries.length;
    setEntries([
      ...entries,
      { account_id: '', account_name: '', debit: '', credit: '', remark: '' },
    ]);
    setFilteredAccounts((prev) => ({ ...prev, [newIndex]: accounts }));
  };

  const removeEntry = (index) => {
    if (entries.length === 1) {
      setAlert({
        show: true,
        message: 'At least one entry is required.',
        variant: 'warning',
      });
      return;
    }
    const updatedEntries = [...entries];
    updatedEntries.splice(index, 1);
    setEntries(updatedEntries);
  };

  const calculateTotals = () => {
    let totalDebit = 0,
      totalCredit = 0;
    entries.forEach((entry) => {
      totalDebit += parseFloat(entry.debit || 0);
      totalCredit += parseFloat(entry.credit || 0);
    });
    return { totalDebit, totalCredit };
  };

  const { totalDebit, totalCredit } = calculateTotals();
  const isBalanced = totalDebit === totalCredit && totalDebit > 0;

  const submitJournal = async () => {
    if (!isBalanced) {
      setAlert({
        show: true,
        message: 'Debits and Credits must be equal before submitting.',
        variant: 'warning',
      });
      return;
    }

    const hasInvalidEntry = entries.some((entry) => !entry.account_id);
    if (hasInvalidEntry) {
      setAlert({
        show: true,
        message: 'Please select valid accounts for all entries.',
        variant: 'warning',
      });
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    const transformedEntries = entries.flatMap((entry) => {
      const formattedEntries = [];
      if (entry.debit && parseFloat(entry.debit) > 0) {
        formattedEntries.push({
          account_id: entry.account_id,
          amount: parseFloat(entry.debit),
          debit_credit: 'D',
          remark: entry.remark || '',
        });
      }
      if (entry.credit && parseFloat(entry.credit) > 0) {
        formattedEntries.push({
          account_id: entry.account_id,
          amount: parseFloat(entry.credit),
          debit_credit: 'C',
          remark: entry.remark || '',
        });
      }
      return formattedEntries;
    });

    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create-journal`,
        {
          date,
          description,
          entries: transformedEntries,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: 'Journal entry added successfully!',
          variant: 'success',
        });
        setEntries([
          {
            account_id: '',
            account_name: '',
            debit: '',
            credit: '',
            remark: '',
          },
        ]);
        setDescription('');
        // Keep the same date for next entry
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Error creating journal.',
          variant: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error adding journal entry',
        variant: 'danger',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaBook style={{ fontSize: '1.8rem' }} />
                Create Journal Entry
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                Record double-entry accounting transactions
              </p>
            </div>
            <Button
              variant="light"
              onClick={() => navigate('/journal-list')}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaList /> View All Journals
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Form Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            maxWidth: '1000px',
            margin: '0 auto',
          }}
        >
          <Form>
            {/* Date and Description */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
              <Form.Group style={{ flex: '1' }}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  Date *
                </Form.Label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{
                    borderRadius: '6px',
                    padding: '10px 15px',
                    border: '1px solid #dee2e6',
                    fontSize: '0.9rem',
                  }}
                />
              </Form.Group>
              <Form.Group style={{ flex: '2' }}>
                <Form.Label
                  style={{
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.9rem',
                  }}
                >
                  Description
                </Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this journal entry"
                  style={{
                    borderRadius: '6px',
                    padding: '10px 15px',
                    border: '1px solid #dee2e6',
                    fontSize: '0.9rem',
                  }}
                />
              </Form.Group>
            </div>

            {/* Entries Table */}
            <div style={{ marginBottom: '20px', overflowX: 'auto' }}>
              <Table hover style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '30%',
                      }}
                    >
                      Account
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '15%',
                        textAlign: 'right',
                      }}
                    >
                      Debit
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '15%',
                        textAlign: 'right',
                      }}
                    >
                      Credit
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '35%',
                      }}
                    >
                      Remark
                    </th>
                    <th
                      style={{
                        padding: '12px',
                        fontWeight: '600',
                        color: '#495057',
                        width: '10%',
                        textAlign: 'center',
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td style={{ padding: '12px' }}>
                        <div
                          ref={(el) => (dropdownRefs.current[index] = el)}
                          style={{ position: 'relative' }}
                        >
                          <Form.Control
                            value={entry.account_name}
                            onChange={(e) =>
                              handleAccountChange(index, e.target.value)
                            }
                            onFocus={() =>
                              setShowDropdowns((prev) => ({
                                ...prev,
                                [index]: true,
                              }))
                            }
                            placeholder="Search account..."
                            style={{
                              borderRadius: '6px',
                              padding: '8px 35px 8px 12px',
                              border: entry.account_id
                                ? '1px solid #28a745'
                                : '1px solid #dee2e6',
                              backgroundColor: entry.account_id
                                ? '#f0fff4'
                                : 'white',
                              fontSize: '0.9rem',
                            }}
                          />
                          {entry.account_id && (
                            <FaCheck
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#28a745',
                                fontSize: '0.9rem',
                              }}
                            />
                          )}
                          {showDropdowns[index] &&
                            filteredAccounts[index]?.length > 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '6px',
                                  marginTop: '5px',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  zIndex: 1000,
                                }}
                              >
                                {filteredAccounts[index].map((account) => (
                                  <div
                                    key={account.id}
                                    onClick={() =>
                                      selectAccount(index, account)
                                    }
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f0f0f0',
                                      fontSize: '0.85rem',
                                      transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.backgroundColor =
                                        '#f8f9ff')
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.backgroundColor = 'white')
                                    }
                                  >
                                    {account.name}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={entry.debit}
                          onChange={(e) =>
                            handleChange(index, 'debit', e.target.value)
                          }
                          onWheel={(e) => e.target.blur()}
                          placeholder="0.00"
                          style={{
                            borderRadius: '6px',
                            padding: '8px 12px',
                            border: '1px solid #dee2e6',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={entry.credit}
                          onChange={(e) =>
                            handleChange(index, 'credit', e.target.value)
                          }
                          onWheel={(e) => e.target.blur()}
                          placeholder="0.00"
                          style={{
                            borderRadius: '6px',
                            padding: '8px 12px',
                            border: '1px solid #dee2e6',
                            fontSize: '0.9rem',
                            textAlign: 'right',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Form.Control
                          type="text"
                          value={entry.remark}
                          onChange={(e) =>
                            handleChange(index, 'remark', e.target.value)
                          }
                          placeholder="Optional remark..."
                          style={{
                            borderRadius: '6px',
                            padding: '8px 12px',
                            border: '1px solid #dee2e6',
                            fontSize: '0.9rem',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {entries.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeEntry(index)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                            }}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderTop: '2px solid #dee2e6',
                    fontWeight: '600',
                  }}
                >
                  <tr>
                    <td style={{ padding: '12px', color: '#495057' }}>
                      Totals
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        textAlign: 'right',
                        color: isBalanced ? '#28a745' : '#dc3545',
                        fontSize: '1rem',
                      }}
                    >
                      ₹{totalDebit.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        textAlign: 'right',
                        color: isBalanced ? '#28a745' : '#dc3545',
                        fontSize: '1rem',
                      }}
                    >
                      ₹{totalCredit.toFixed(2)}
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Balance Status */}
            {!isBalanced &&
              totalDebit !== totalCredit &&
              (totalDebit > 0 || totalCredit > 0) && (
                <div
                  style={{
                    padding: '12px 20px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    color: '#856404',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                  }}
                >
                  ⚠️ Journal is not balanced. Difference: ₹
                  {Math.abs(totalDebit - totalCredit).toFixed(2)}
                </div>
              )}

            {isBalanced && (
              <div
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #28a745',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  color: '#155724',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                }}
              >
                ✓ Journal is balanced
              </div>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outline-success"
                onClick={addEntry}
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaPlus /> Add Entry
              </Button>

              <div style={{ display: 'flex', gap: '10px' }}>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setEntries([
                      {
                        account_id: '',
                        account_name: '',
                        debit: '',
                        credit: '',
                      },
                    ]);
                    setDescription('');
                    setAlert({ show: false, message: '', variant: '' });
                  }}
                  disabled={loading}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FaTimes /> Clear
                </Button>
                <Button
                  variant="primary"
                  onClick={submitJournal}
                  disabled={!isBalanced || loading}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontWeight: '600',
                    background: isBalanced
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#6c757d',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Submitting...
                    </>
                  ) : (
                    <>
                      <FaCheck /> Submit Journal
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Layout>
  );
};

export default Journal;
